import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import '../styles/base.css';

/**
 * Wrapper every redesigned page renders inside.
 *
 * It carries three things the redesigned pages depend on and cannot get from
 * the app:
 *
 *  - `dir`, which the app otherwise never sets (it swaps stylesheets instead).
 *    Without it Tailwind's `rtl:` variants, which compile to `[dir="rtl"]`,
 *    never match, and the page's direction depends on which global sheet
 *    happens to be loaded.
 *  - `lang`, so screen readers switch voice and the browser hyphenates right.
 *  - `.v2-root`, which base.css uses to win the font back from rtl.css.
 *
 * Keeping this in one component means the old pages are unaffected: the one
 * thing it puts on <html> is taken off again the moment a redesigned page is
 * no longer on screen.
 *
 * `smoothScroll={false}` is for a root that wraps chrome rather than a page —
 * `StudentLayout` puts the new header over the old exam and bank pages this
 * way. Smooth scrolling is a property of the document, and the page underneath
 * is not a redesigned one, so it is not this root's to switch on.
 */
const V2Root = ({ className = '', smoothScroll = true, children }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language?.startsWith('ar');

    /*
     * Smooth scrolling for the links that move within a page — the section bar
     * on the certificate page, the hero's button down to the packages.
     *
     * `scroll-behavior` only counts on the element that actually scrolls, and
     * that is <html>, not this wrapper: a rule on `.v2-root` would be read and
     * ignored. So the class goes on the document and comes off on unmount,
     * which is what keeps the admin and student sections — and the old landing
     * pages — jumping exactly as they do today.
     */
    useEffect(() => {
        if (!smoothScroll) return undefined;

        const root = document.documentElement;

        root.classList.add('v2-smooth-scroll');
        return () => root.classList.remove('v2-smooth-scroll');
    }, [smoothScroll]);

    return (
        <div
            dir={isArabic ? 'rtl' : 'ltr'}
            lang={isArabic ? 'ar' : 'en'}
            className={`v2-root tw-font-v2 tw-min-h-screen ${className}`}
        >
            {children}
        </div>
    );
};

export default V2Root;
